import { runsOnDevice } from "./api/client.js";

/**
 * The app's name and icon, from the build it runs in. One web bundle serves every
 * app (native/android/app/build.gradle, flavors): SliqueTV — the website and the
 * sideload APK — and H IPTV (was TV My Way), the Google Play APK. Only the name, the icon and
 * how people pay differ between them.
 *
 * In the APK the native side answers (DistributionPlugin) once, before the first
 * render (main.tsx), while the launch cover still hides the page — so no screen
 * ever shows one name and then the other. Everywhere else it is SliqueTV.
 *
 * Read it at render time (`brand().appName`), never copy it into a module
 * constant: a constant would capture the default before loadBrand() answers.
 */
export type Channel = "web" | "sideload" | "play" | "tenant";

/** One of a white-label tenant's portals, built into its app. */
export interface Portal {
  label: string;
  url: string;
}

export interface Brand {
  appName: string;
  /** The square app icon, for the nav rail and Settings. */
  icon: string;
  channel: Channel;
  /**
   * A tenant's portals (channel "tenant"); empty everywhere else. When there are
   * any, viewers pick one of them to sign in and can never type an address of
   * their own (ADR amendment 2026-10-08).
   */
  portals: Portal[];
  /** A tenant's own country ("Your country"'s automatic choice, settings/countryStore.ts), when it set one. */
  country?: string;
}

const SLIQUETV = { appName: "SliqueTV", icon: "/icon-192.png" };
const HIPTV = { appName: "H IPTV", icon: "/brand/hiptv-192.png" };

/** A start-up that waits on the native side must never hang on it. */
const LOAD_WAIT_MAX_MS = 1_500;

/** A transparent pixel: a tenant's app never shows SliqueTV's icon, even if its own is missing. */
const NO_ICON = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

let current: Brand = { ...SLIQUETV, channel: "web", portals: [] };

export function brand(): Brand {
  return current;
}

export async function loadBrand(): Promise<void> {
  if (!runsOnDevice()) return;
  const cap = (globalThis as { Capacitor?: { nativePromise?: (plugin: string, method: string, options: object) => Promise<unknown> } })
    .Capacitor;
  if (typeof cap?.nativePromise !== "function") return;
  const answer = await Promise.race([
    cap.nativePromise("Distribution", "info", {}).catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_WAIT_MAX_MS)),
  ]);
  const info = answer as { channel?: unknown; appName?: unknown; icon?: unknown; portals?: unknown; colors?: unknown; country?: unknown } | null;
  if (info?.channel === "tenant") {
    current = {
      appName: typeof info.appName === "string" && info.appName ? info.appName : "TV",
      icon: typeof info.icon === "string" && info.icon ? info.icon : NO_ICON,
      channel: "tenant",
      portals: cleanPortals(info.portals),
      ...(typeof info.country === "string" && /^[A-Z]{2}$/.test(info.country) ? { country: info.country } : {}),
    };
    applyColors(info.colors);
    return;
  }
  // An APK from before the build switch has no Distribution plugin: it is SliqueTV.
  current = info?.channel === "play" ? { ...HIPTV, channel: "play", portals: [] } : { ...SLIQUETV, channel: "sideload", portals: [] };
}

function cleanPortals(value: unknown): Portal[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const url = typeof item?.url === "string" ? item.url.trim() : "";
    if (!/^https?:\/\//.test(url)) return [];
    const label = typeof item?.label === "string" && item.label.trim() ? item.label.trim() : url;
    return [{ label, url }];
  });
}

const HEX = /^#[0-9a-f]{6}$/i;

function rgb(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function mix(a: string, b: string, weight: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const part = (x: number, y: number) => Math.round(x + (y - x) * weight).toString(16).padStart(2, "0");
  return `#${part(ar, br)}${part(ag, bg)}${part(ab, bb)}`;
}

/**
 * A tenant's four colours (its partner page) onto the app's tokens
 * (packages/ui/src/tokens.css): background, surface, accent and text, with the
 * in-between shades worked out from them.
 */
function applyColors(value: unknown): void {
  const colors = (value ?? {}) as Record<string, unknown>;
  const pick = (key: string) => (typeof colors[key] === "string" && HEX.test(colors[key] as string) ? (colors[key] as string) : null);
  const background = pick("background");
  const surface = pick("surface");
  const accent = pick("accent");
  const text = pick("text");
  const root = document.documentElement.style;
  if (background) root.setProperty("--bg-0", background);
  if (background && surface) root.setProperty("--bg-1", mix(background, surface, 0.5));
  if (surface) {
    root.setProperty("--bg-2", surface);
    root.setProperty("--bg-3", mix(surface, "#ffffff", 0.06));
  }
  if (text) root.setProperty("--text-hi", text);
  if (accent) {
    const [r, g, b] = rgb(accent);
    root.setProperty("--accent", accent);
    root.setProperty("--accent-hi", mix(accent, "#ffffff", 0.3));
    root.setProperty("--accent-soft", `rgba(${r}, ${g}, ${b}, 0.16)`);
    root.setProperty("--accent-glow", `rgba(${r}, ${g}, ${b}, 0.45)`);
  }
}
